import { createSelector, type MemoizedSelector } from '@ngrx/store';
import {
  INITIAL_RESPONDENT_LIST_UPLOAD_STATE,
  type RespondentListUploadState,
  type SurveyLaunchFormState,
} from '@ps/survey-utils';
import { surveyCreationFeature } from '../survey-creation.feature';
import { DEFAULT_CHILD_LAUNCH_STATE } from '../survey-creation-state.constants';
import { selectSurvey } from '../core/survey-creation-core.selectors';
import { resolveActiveChildKey } from './utils/launch-children.helper';
import { SurveyChildKey } from '../survey-creation-shared-state.model';

const { selectLaunch, selectCore } = surveyCreationFeature;

// Uploads live beside the children rather than in them, so projections take a key.
type KeySelector = MemoizedSelector<object, SurveyChildKey>;

/** The child currently being worked on — see `resolveActiveChildKey`. */
const selectActiveLaunchKey: KeySelector = createSelector(
  selectLaunch,
  selectCore,
  (state, core) => resolveActiveChildKey(state, core.activeChildKey),
);

// Any child by key, for work that runs on a child other than the active one.
const fixedKey = (key: SurveyChildKey): KeySelector =>
  createSelector(selectLaunch, () => key);

// Shared by the active child and any child by key; both fall back to defaults
// so downstream selectors never have to null-check a missing entry.
const launchFormOf = (
  key$: KeySelector,
): MemoizedSelector<object, SurveyLaunchFormState> =>
  createSelector(
    selectLaunch,
    key$,
    (state, key) => state.children[key] ?? DEFAULT_CHILD_LAUNCH_STATE,
  );

const respondentListUploadOf = (
  key$: KeySelector,
): MemoizedSelector<object, RespondentListUploadState> =>
  createSelector(
    selectLaunch,
    key$,
    (state, key) =>
      state.respondentListUploads[key] ?? INITIAL_RESPONDENT_LIST_UPLOAD_STATE,
  );

export const selectLaunchForm = launchFormOf(selectActiveLaunchKey);

/** The active child's respondent-list upload UI state */
export const selectRespondentListUpload = respondentListUploadOf(
  selectActiveLaunchKey,
);

/**
 * The server-resolved URL the TEST button opens. Lives on the survey itself,
 * so saving or patching keeps it current without anything else tracking it.
 */
export const selectTestEntryLink = createSelector(
  selectSurvey,
  // `|| null` so a blank link reads as "no link" rather than opening one.
  (survey) => survey?.testPsSurveyEntryLink || null,
);

export const selectLaunchPricing = createSelector(
  selectLaunch,
  (state) => state.pricing,
);

export const selectIsLaunchPricingLoading = createSelector(
  selectLaunch,
  (state) => state.isPricingLoading,
);

export const selectIsLaunching = createSelector(
  selectLaunch,
  (state) => state.isLaunching,
);

export const selectAidaEligibility = createSelector(
  selectLaunch,
  (state) => state.aidaEligibility,
);

export const selectLaunchSameSettings = createSelector(
  selectLaunch,
  (state) => state.sameSettings,
);

/** Every child's launch form as the launch page last held it, keyed by survey id. */
export const selectLaunchChildren = createSelector(
  selectLaunch,
  (state) => state.children,
);

// Per child — the same projections over a child by key.
export const selectLaunchFormFor = (
  key: SurveyChildKey,
): MemoizedSelector<object, SurveyLaunchFormState> =>
  launchFormOf(fixedKey(key));

export const selectRespondentListUploadFor = (
  key: SurveyChildKey,
): MemoizedSelector<object, RespondentListUploadState> =>
  respondentListUploadOf(fixedKey(key));
